import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MerchantProvisionRepository } from '@app/domain/ports/tpv/merchant-provision.repository';
import { MerchantProvisionResult } from '@app/domain/models/tpv/merchant.model';
import {
  ProvisionAssistedRequest,
  ProvisionMerchantRequest,
  ProvisionMerchantCompanyAnchoredRequest,
  ProvisionResponseDto,
} from '@app/data/dtos/tpv/provision.dto';
import { mapProvisionResult } from '@app/data/mappers/tpv/provision.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';

/**
 * Implementación HTTP del alta de comercios (`api/v1/tpv/merchants/provision`).
 *
 * Auth: NO setea `x-api-key` ni `Skip-Auth-Interceptor`. El `authInterceptor`
 * agrega el Bearer y maneja 401/refresh; el `ApiKeyInterceptor` agrega el
 * `x-api-key` para URLs propias. Solo se añade `Idempotency-Key` (obligatoria).
 */
@Injectable()
export class MerchantProvisionRemoteDataSource extends MerchantProvisionRepository {
  private readonly http = inject(HttpClient);

  override provision(
    request: ProvisionMerchantRequest,
    idempotencyKey: string,
  ): Observable<MerchantProvisionResult> {
    return this.http
      .post<TpvEnvelope<ProvisionResponseDto> | ProvisionResponseDto>(
        tpvUrl('merchants/provision/'),
        request,
        { headers: this.idempotencyHeaders(idempotencyKey) },
      )
      .pipe(map((resp) => mapProvisionResult(unwrapTpv(resp))));
  }

  override provisionAssisted(
    request: ProvisionAssistedRequest,
    idempotencyKey: string,
  ): Observable<MerchantProvisionResult> {
    return this.http
      .post<TpvEnvelope<ProvisionResponseDto> | ProvisionResponseDto>(
        tpvUrl('merchants/provision-assisted/'),
        request,
        { headers: this.idempotencyHeaders(idempotencyKey) },
      )
      .pipe(map((resp) => mapProvisionResult(unwrapTpv(resp))));
  }

  override provisionCompanyAnchored(
    idCompany: string,
    request: ProvisionMerchantCompanyAnchoredRequest,
    idempotencyKey: string,
  ): Observable<MerchantProvisionResult> {
    return this.http
      .post<TpvEnvelope<ProvisionResponseDto> | ProvisionResponseDto>(
        tpvUrl(`companies/${idCompany}/merchants/provision/`),
        request,
        { headers: this.idempotencyHeaders(idempotencyKey) },
      )
      .pipe(map((resp) => mapProvisionResult(unwrapTpv(resp))));
  }

  private idempotencyHeaders(idempotencyKey: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    });
  }
}
